import { useAxleMutation } from '@/fetching/apiHelpers';
import { organizationBasicSchema } from '@/fetching/sharedSchemas/basicSchemas';
import { z } from 'zod';

export const useLogin = () => {
    return useAxleMutation({
        axiosConfig: ({
            email,
            password,
            otp,
        }: {
            email: string;
            password: string;
            otp: string;
        }) => ({
            method: 'post',
            path: '/auth',
            data: {
                email,
                password,
                otp,
            },
        }),
        responseSchema: z.object({
            uuid: z.string(),
            organization: organizationBasicSchema,
        }),
        defaultSuccessMessage: '',
        defaultErrorMessage: 'Failed to connect to server. Please try again.',
        softResetQueries: [
            [], // Invalidate all queries.
        ],
        // Normally, we deduplicate toast messages. But in this case,
        //  we want multiple "Incorrect email and password" messages
        //  or such to show up, one for each login attempt.
        allowDuplicateToasts: true,
    });
};

// Get the URL to redirect the user to in order to sign in via SSO.
export const useGetSSOLoginUrl = () =>
    useAxleMutation({
        axiosConfig: ({ email, state }: { email: string; state: string }) => ({
            method: 'post',
            path: '/sso',
            data: {
                email: email,
                state: state,
            },
        }),
        responseSchema: z.object({
            redirect_url: z.string(),
        }),
        transform: (data) => ({
            redirectUrl: data.redirect_url,
        }),
        defaultSuccessMessage: 'Redirecting you to your organization’s login page.',
        defaultErrorMessage: 'No organization found for this email address.',
        softResetQueries: [
            [], // Invalidate all queries.
        ],
        allowDuplicateToasts: true,
    });

// Uses the code from the SSO callback URL to fetch the Axle User
//  authenticated via the IDP and sign them in.
export const useLoginWithSSOCode = () =>
    useAxleMutation({
        axiosConfig: ({ code }: { code: string }) => ({
            method: 'post',
            path: '/sso-callback',
            data: {
                code: code,
            },
        }),
        responseSchema: z.object({
            uuid: z.string(),
            organization: organizationBasicSchema,
            email: z.string(),
        }),
        defaultSuccessMessage: '',
        defaultErrorMessage: '',
    });

export const useLogoutOnServer = () =>
    useAxleMutation({
        axiosConfig: () => ({
            method: 'post',
            path: '/logout',
        }),
    });
